import { SMB_FY_QUARTER, SMB_PERCENT_FRACTION_DIGITS, SMB_TOTAL_PERCENT } from '../configs'
import { IPlanningSmb, ISmbNode, TSmbSplitResult } from '../model/types'

import {
  applySmbEditToStore,
  isSameDirectSmbEdit,
  wouldSmbEditDiscardGenuineData,
} from './applySmbEdit'
import { getSmbNodeId } from './buildSmbTree'
import { TGetSmbWeight } from './getSmbEffectiveValue'
import { mapPlanningSmbToStore, mapStoreToPlanningSmbRows } from './mapPlanningSmb'
import { getEuPercent, normalizeSmbPercent } from './smbSplitStore'

export type TGuidedSmbClassification = 'smb' | 'eu' | 'neutral'
export type TGuidedSmbTrend = 'up' | 'down' | null

export interface IGuidedSmbPlanResult {
  smb: number
  eu: number
  isClamped: boolean
}

export const getGuidedSmbPlanResult = (
  referenceSmb: number,
  delta: number
): IGuidedSmbPlanResult => {
  const finiteDelta = Number.isFinite(delta) ? delta : 0
  const rawSmb = referenceSmb + finiteDelta
  const smb = normalizeSmbPercent(rawSmb)

  return {
    smb,
    eu: getEuPercent(smb),
    isClamped: rawSmb < 0 || rawSmb > SMB_TOTAL_PERCENT,
  }
}

export const getGuidedSmbClassification = (
  split: TSmbSplitResult
): TGuidedSmbClassification | null => {
  if (split.isEmpty) {
    return null
  }
  if (split.smb >= 70) {
    return 'smb'
  }
  if (split.eu >= 70) {
    return 'eu'
  }

  return 'neutral'
}

export const getGuidedSmbTrend = (value: number, baseline: number): TGuidedSmbTrend => {
  const difference = value - baseline

  if (Math.abs(difference) < 0.5) {
    return null
  }

  return difference > 0 ? 'up' : 'down'
}

export const getGuidedSmbInitialDelta = (referenceSmb: number, plannedSmb?: number): number => {
  if (plannedSmb === undefined) {
    return 0
  }

  const factor = 10 ** SMB_PERCENT_FRACTION_DIGITS

  return Math.round((plannedSmb - referenceSmb) * factor) / factor
}

export interface IBuildGuidedSmbPlanRows {
  planningSmb: IPlanningSmb[]
  tree: ISmbNode
  partnerId: string
  programType: string
  years: readonly number[]
  smb: number
  getWeight: TGetSmbWeight
  confirmOverwrite: (year: number) => Promise<boolean>
}

export interface IGuidedSmbPlanRowsResult {
  hasChanges: boolean
  rows: IPlanningSmb[]
}

/**
 * Applies one Offering-level annual target to every planning year. Only a destructive
 * overwrite of the current planning year needs confirmation; future years are applied
 * without repeating the same dialog.
 */
export const buildGuidedSmbPlanRows = async ({
  planningSmb,
  tree,
  partnerId,
  programType,
  years,
  smb,
  getWeight,
  confirmOverwrite,
}: IBuildGuidedSmbPlanRows): Promise<IGuidedSmbPlanRowsResult> => {
  const context = { partnerId, programType, years }
  const store = mapPlanningSmbToStore(planningSmb, context, tree)
  const changedYears = new Set<number>()
  const currentYear = years[0]

  for (const year of years) {
    const edit = {
      node: tree,
      ancestors: [],
      year,
      fq: SMB_FY_QUARTER,
      smb,
    }
    const wouldDiscard = wouldSmbEditDiscardGenuineData(store, tree, year, SMB_FY_QUARTER)

    if (!wouldDiscard && isSameDirectSmbEdit(store, edit)) {
      continue
    }
    if (wouldDiscard && year === currentYear && !(await confirmOverwrite(year))) {
      continue
    }

    applySmbEditToStore(store, {
      ...edit,
      getWeight,
      // Guided Setup deliberately authors one annual Offering value for every year.
      deriveFyFromQuarters: false,
    })
    changedYears.add(year)
  }

  const rows = mapStoreToPlanningSmbRows(store, tree, context).map((row) => {
    const nodeId = getSmbNodeId(row.ProgramType, row.Category, row.SubCategory, row.L3Category)

    return nodeId === tree.id && changedYears.has(row.FYear) ? { ...row, IsCollapsed: 1 } : row
  })

  return { hasChanges: changedYears.size > 0, rows }
}
